import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { emailTemplateService } from '../../../services/emailTemplateService';

const TemplatePreview = ({ template, onClose }) => {
  const [device, setDevice] = useState('desktop');
  const [sampleClient, setSampleClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSampleClients();
  }, [template]);

  const loadSampleClients = async () => {
    try {
      setLoading(true);
      const segmentedClients = await emailTemplateService?.getSegmentedClients(template?.segment);
      setClients(segmentedClients || []);
      if (segmentedClients?.length > 0) {
        setSampleClient(segmentedClients?.[0]);
      }
    } catch (error) {
      console.error('Error loading sample clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const previewHtml = sampleClient
    ? emailTemplateService?.replaceVariables(template?.templateBody, sampleClient)
    : template?.templateBody;

  const previewSubject = sampleClient
    ? emailTemplateService?.replaceVariables(template?.subjectLine, sampleClient)
    : template?.subjectLine;

  const deviceSizes = {
    desktop: 'w-full',
    tablet: 'w-[768px]',
    mobile: 'w-[375px]'
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevation-3 w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-heading font-bold text-foreground">
              Template Preview
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {template?.templateName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-smooth press-effect focus-ring"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-foreground">Sample Client:</label>
            <select
              value={sampleClient?.id || ''}
              onChange={(e) => {
                const client = clients?.find(c => c?.id === e?.target?.value);
                setSampleClient(client);
              }}
              className="px-3 py-1 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground text-sm"
              disabled={loading}
            >
              {clients?.map((client) => (
                <option key={client?.id} value={client?.id}>
                  {client?.name} - ${client?.totalRevenue?.toFixed(2)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setDevice('desktop')}
              className={`p-2 rounded-lg transition-smooth ${
                device === 'desktop' ?'bg-primary text-primary-foreground' :'bg-background text-foreground hover:bg-muted'
              }`}
              title="Desktop View"
            >
              <Icon name="Monitor" size={20} />
            </button>
            <button
              onClick={() => setDevice('tablet')}
              className={`p-2 rounded-lg transition-smooth ${
                device === 'tablet'
                  ? 'bg-primary text-primary-foreground' :'bg-background text-foreground hover:bg-muted'
              }`}
              title="Tablet View"
            >
              <Icon name="Tablet" size={20} />
            </button>
            <button
              onClick={() => setDevice('mobile')}
              className={`p-2 rounded-lg transition-smooth ${
                device === 'mobile' ?'bg-primary text-primary-foreground' :'bg-background text-foreground hover:bg-muted'
              }`}
              title="Mobile View"
            >
              <Icon name="Smartphone" size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6 bg-muted/20">
          <div className="flex justify-center">
            <div className={`${deviceSizes?.[device]} transition-all duration-300`}>
              <div className="bg-white border border-border rounded-lg shadow-lg overflow-hidden">
                <div className="bg-gray-100 border-b border-gray-300 p-4">
                  <div className="text-sm text-gray-600 mb-1">Subject:</div>
                  <div className="font-semibold text-gray-900">{previewSubject}</div>
                </div>
                <div
                  className="p-6 text-black"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                  style={{
                    fontFamily: 'Arial, sans-serif',
                    lineHeight: '1.6',
                    color: '#333'
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-border bg-muted/30">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth press-effect focus-ring"
          >
            Close Preview
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreview;